// La fila de miniaturas de lo ELEGIDO en "Elegí qué agregar", a la izquierda del botón
// (ADR-2026-10-05-el-pie-del-selector-muestra-lo-elegido). Cada una con su X en la esquina
// para sacarla. Entran y salen con una animación corta: la que se va sigue dibujada los
// milisegundos que dura su salida (por eso la lista que se dibuja es propia y no la de las
// props), así sacar una desde la X o desmarcándola en la grilla se ve igual.

import React, { useEffect, useRef, useState } from 'react';
import { FileText, X } from 'lucide-react';

export interface MiniaturaElegida {
  id: string;
  url: string | null;
  nombre: string;
}

/** Lo que dura la salida (igual que `miniatura-sale` en index.css). */
const SALIDA_MS = 180;
/** Lo que dura la entrada (`miniatura-entra`). */
const ENTRADA_MS = 220;

export const MiniaturasElegidas: React.FC<{
  items: MiniaturaElegida[];
  onQuitar: (id: string) => void;
  className?: string;
  /** "grande" (56 px): la barra del Catálogo. Por default, las de 44 px del selector del agente. */
  tamano?: 'normal' | 'grande';
}> = ({ items, onQuitar, className = '', tamano = 'normal' }) => {
  const [dibujadas, setDibujadas] = useState<Array<MiniaturaElegida & { sale?: boolean }>>(items);
  // Cuáles están ENTRANDO ahora mismo. Va en estado y no en un ref que se toca al dibujar:
  // en StrictMode el render corre dos veces y el segundo ya no las vería como nuevas.
  const [entrando, setEntrando] = useState<ReadonlySet<string>>(() => new Set());
  // Los ids de la vuelta anterior: lo que se compara para saber qué entró y qué salió. Lo
  // que ya estaba al abrir no "entra".
  const previos = useRef<Set<string>>(new Set(items.map((i) => i.id)));

  useEffect(() => {
    const vigentes = new Set(items.map((i) => i.id));
    const nuevas = items.filter((i) => !previos.current.has(i.id)).map((i) => i.id);
    const huboSalida = [...previos.current].some((id) => !vigentes.has(id));
    previos.current = vigentes;
    if (nuevas.length === 0 && !huboSalida) {
      // Sólo cambió algo de adentro (una URL que se firmó): se refresca sin animar.
      setDibujadas((prev) => {
        const cambio = prev.some((p) => {
          const i = items.find((x) => x.id === p.id);
          return !!i && (i.url !== p.url || i.nombre !== p.nombre);
        });
        return cambio ? prev.map((p) => items.find((i) => i.id === p.id) ?? p) : prev;
      });
      return;
    }
    if (nuevas.length > 0) setEntrando(new Set(nuevas));
    setDibujadas((prev) => {
      const salientes = prev.filter((p) => !vigentes.has(p.id)).map((p) => ({ ...p, sale: true }));
      // Las vigentes en SU orden; las que salen, donde estaban.
      const out: Array<MiniaturaElegida & { sale?: boolean }> = [...items];
      for (const s of salientes) {
        const i = prev.findIndex((p) => p.id === s.id);
        out.splice(Math.min(Math.max(i, 0), out.length), 0, s);
      }
      return out;
    });
    const t = window.setTimeout(() => {
      setDibujadas((prev) => prev.filter((p) => !p.sale));
      setEntrando(new Set());
    }, Math.max(SALIDA_MS, ENTRADA_MS));
    return () => window.clearTimeout(t);
  }, [items]);

  if (dibujadas.length === 0) return null;
  return (
    <div className={`min-w-0 items-center gap-2 overflow-x-auto py-1.5 pr-1 scrollbar-hide ${className}`}>
      {dibujadas.map((m) => {
        const nueva = !m.sale && entrando.has(m.id);
        return (
          <span
            key={m.id}
            title={m.nombre}
            // El destino de un vuelo desde la grilla (lo usa el pie del Catálogo).
            data-miniatura-elegida={m.id}
            className={`group/mini relative block shrink-0 ${tamano === 'grande' ? 'h-14 w-14' : 'h-11 w-11'} ${m.sale ? 'animate-miniatura-sale' : nueva ? 'animate-miniatura-entra' : ''}`}
          >
            <span className={`block h-full w-full overflow-hidden bg-[#F0F2F7] ring-1 ring-black/[0.06] ${tamano === 'grande' ? 'rounded-xl' : 'rounded-[10px]'}`}>
              {m.url ? (
                <img src={m.url} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="flex h-full w-full items-center justify-center text-[#02062B]/[0.4]">
                  <FileText className="h-4 w-4" aria-hidden="true" />
                </span>
              )}
            </span>
            {!m.sale && (
              <button
                type="button"
                onClick={() => onQuitar(m.id)}
                aria-label={`Quitar ${m.nombre}`}
                className="absolute -right-1.5 -top-1.5 inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-brand-black text-white shadow-sm transition-transform hover:scale-110"
              >
                <X className="h-2.5 w-2.5" strokeWidth={3} />
              </button>
            )}
          </span>
        );
      })}
    </div>
  );
};

export default MiniaturasElegidas;
